/**
 * Incident Editor Dialog Footer Actions (PROJ-007)
 * PROJ-017: RBAC Archive/Delete System Integration
 * Provides action buttons and error feedback
 * Pattern: Opción B Híbrido
 */

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Save, CheckCircle, X, Trash2, Archive, ArchiveRestore } from 'lucide-react';
import { CanAccess } from '@/components/auth/CanAccess';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';
import type { Entity } from '@/hooks/usePermission';

interface FooterActionsProps {
  form: UseFormReturn<IncidentFormData>;
  submitError: string | null;
  setSubmitError: (error: string | null) => void;
  handleSaveAndClose: () => Promise<void>;
  requestClose: () => void;
  entity?: Entity;
  recordId?: string | null;
  isArchived?: boolean;
  onArchive?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
}

export function FooterActions({
  form,
  submitError,
  setSubmitError,
  handleSaveAndClose,
  requestClose,
  entity = 'incidents',
  recordId,
  isArchived = false,
  onArchive,
  onRestore,
  onDelete,
}: FooterActionsProps) {
  const isSubmitting = form.formState.isSubmitting;
  const isEditMode = !!recordId;

  return (
    <TooltipProvider>
      {submitError && (
        <div className="relative z-50 border-t border-destructive/20 bg-destructive/10 px-6 py-3">
          <div className="flex items-start gap-3">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
              <span className="text-xs font-bold">!</span>
            </div>
            <div className="flex-1 space-y-1">
              <p className="text-sm font-medium text-destructive">Error Al Guardar Incidente</p>
              <p className="text-xs text-destructive/90">{submitError}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSubmitError(null)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t bg-background px-6 py-4">
        {/* LEFT: Destructive actions (permission-controlled) */}
        <div className="flex gap-2">
          {isEditMode && onDelete && (
            <CanAccess entity={entity} privilege="DELETE">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onDelete}
                    disabled={isSubmitting}
                    className="text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                    aria-label="Eliminar permanentemente"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Eliminar permanentemente</TooltipContent>
              </Tooltip>
            </CanAccess>
          )}

          {isEditMode && (onArchive || onRestore) && (
            <CanAccess entity={entity} privilege="ARCHIVE">
              {isArchived && onRestore ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={onRestore}
                      disabled={isSubmitting}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      aria-label="Restaurar registro"
                    >
                      <ArchiveRestore className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Restaurar registro</TooltipContent>
                </Tooltip>
              ) : onArchive ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={onArchive}
                      disabled={isSubmitting}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      aria-label="Archivar registro"
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Archivar registro</TooltipContent>
                </Tooltip>
              ) : null}
            </CanAccess>
          )}
        </div>

        {/* RIGHT: Primary actions (always visible) */}
        <div className="flex items-center gap-2">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </Button>

          <Button
            type="button"
            onClick={handleSaveAndClose}
            disabled={isSubmitting}
            className="gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            Guardar y Cerrar
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={requestClose}
                disabled={isSubmitting}
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Cerrar</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}
